import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/classUtils';
import {
  useAdminCreateUpdateService,
  useAdminReferencesService,
  useAdminSourcesService,
  useAdminUploadUpdateFileService,
  useAdminVersionsService,
} from '@/services/backoffice';
import { UPDATE_TYPE_OPTIONS } from '@/pages/RegulatoryWatch/labels';
import dayjs from 'dayjs';
import { FileText, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState, type FC } from 'react';

interface CreateUpdateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type UpdateKind = 'regulatory_update' | 'news';

export const CreateUpdateModal: FC<CreateUpdateModalProps> = ({
  open,
  onOpenChange,
}) => {
  const [kind, setKind] = useState<UpdateKind>('regulatory_update');

  // Regulatory update fields
  const [referenceId, setReferenceId] = useState('');
  const [updateType, setUpdateType] = useState('');
  const [versionId, setVersionId] = useState('');
  const [refSearch, setRefSearch] = useState('');

  // News fields
  const [sourceId, setSourceId] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [shortSummary, setShortSummary] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Shared fields
  const [summary, setSummary] = useState('');
  const [publicationDate, setPublicationDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { references } = useAdminReferencesService({
    search: refSearch || undefined,
  });
  const { versions } = useAdminVersionsService(referenceId || undefined);
  const { sources } = useAdminSourcesService();
  const { createUpdate, isPending: isCreating } = useAdminCreateUpdateService();
  const { uploadFile, isPending: isUploading } =
    useAdminUploadUpdateFileService();

  const isPending = isCreating || isUploading;

  // All sources are available for news updates (type is now on reference, not source)
  const newsSources = sources;

  const selectedReference = references.find(r => r.id === referenceId);

  const handleSubmit = () => {
    if (kind === 'regulatory_update') {
      if (!referenceId || !updateType) return;
      createUpdate(
        {
          kind: 'regulatory_update',
          referenceId,
          updateType,
          summary: summary || undefined,
          publicationDate: publicationDate || undefined,
          versionId: versionId || undefined,
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            resetForm();
          },
        }
      );
    } else {
      if (!sourceId) return;
      createUpdate(
        {
          kind: 'news',
          sourceId,
          articleUrl: articleUrl || undefined,
          title: title || undefined,
          shortSummary: shortSummary || undefined,
          summary: summary || undefined,
          content: content || undefined,
          publicationDate: publicationDate || undefined,
        },
        {
          onSuccess: result => {
            if (selectedFile && result.id) {
              uploadFile(
                { updateId: result.id, file: selectedFile },
                {
                  onSuccess: () => {
                    onOpenChange(false);
                    resetForm();
                  },
                  onError: () => {
                    // Update was created but file upload failed — still close
                    onOpenChange(false);
                    resetForm();
                  },
                }
              );
            } else {
              onOpenChange(false);
              resetForm();
            }
          },
        }
      );
    }
  };

  const resetForm = () => {
    setKind('regulatory_update');
    setReferenceId('');
    setUpdateType('');
    setSummary('');
    setShortSummary('');
    setPublicationDate(new Date().toISOString().split('T')[0]);
    setVersionId('');
    setRefSearch('');
    setSourceId('');
    setArticleUrl('');
    setTitle('');
    setContent('');
    setSelectedFile(null);
  };

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) setSelectedFile(file);
      // Reset input so the same file can be re-selected
      e.target.value = '';
    },
    []
  );

  const isValid =
    kind === 'regulatory_update' ? !!referenceId && !!updateType : !!sourceId;

  return (
    <Dialog
      open={open}
      onOpenChange={val => {
        onOpenChange(val);
        if (!val) resetForm();
      }}
    >
      <DialogContent className="max-w-6xl h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {kind === 'news'
              ? 'New News Update'
              : selectedReference
                ? selectedReference.title
                : 'New Regulatory Update'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex gap-6 overflow-hidden">
          {/* Left side: Information fields */}
          <div className="flex-1 flex flex-col gap-4 overflow-y-auto pr-2">
            {/* Kind toggle */}
            <div className="space-y-2">
              <Label>Update Kind</Label>
              <div className="flex gap-2">
                <Button
                  variant={kind === 'regulatory_update' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setKind('regulatory_update')}
                >
                  Regulatory Update
                </Button>
                <Button
                  variant={kind === 'news' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setKind('news')}
                >
                  News
                </Button>
              </div>
            </div>

            {kind === 'regulatory_update' ? (
              <>
                <div className="space-y-2">
                  <Label>Reference</Label>
                  <Input
                    placeholder="Search references..."
                    value={refSearch}
                    onChange={e => setRefSearch(e.target.value)}
                  />
                  <Select
                    value={referenceId}
                    onValueChange={val => {
                      setReferenceId(val);
                      setVersionId('');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reference" />
                    </SelectTrigger>
                    <SelectContent>
                      {references.map(ref => (
                        <SelectItem key={ref.id} value={ref.id}>
                          {ref.externalId} — {ref.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Update Type</Label>
                    <Select value={updateType} onValueChange={setUpdateType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {UPDATE_TYPE_OPTIONS.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Publication Date</Label>
                    <Input
                      type="date"
                      value={publicationDate}
                      onChange={e => setPublicationDate(e.target.value)}
                    />
                  </div>
                </div>

                {referenceId && (
                  <div className="space-y-2">
                    <Label>Version (optional)</Label>
                    <Select value={versionId} onValueChange={setVersionId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select version" />
                      </SelectTrigger>
                      <SelectContent>
                        {versions.map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.status}
                            {v.effectiveDate
                              ? ` — ${dayjs(v.effectiveDate.substring(0, 10)).format('D MMMM YYYY')}`
                              : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={sourceId} onValueChange={setSourceId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select news source" />
                    </SelectTrigger>
                    <SelectContent>
                      {newsSources.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    placeholder="Full article title..."
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Article URL (optional)</Label>
                  <Input
                    placeholder="https://..."
                    value={articleUrl}
                    onChange={e => setArticleUrl(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Short Summary</Label>
                    <Input
                      placeholder="Headline..."
                      value={shortSummary}
                      onChange={e => setShortSummary(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Publication Date</Label>
                    <Input
                      type="date"
                      value={publicationDate}
                      onChange={e => setPublicationDate(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex-1 flex flex-col space-y-2 min-h-0">
              <Label>Summary</Label>
              <Textarea
                value={summary}
                onChange={e => setSummary(e.target.value)}
                className={cn(
                  'resize-none',
                  kind === 'news' ? 'min-h-[100px]' : 'flex-1 min-h-[200px]'
                )}
                placeholder="Enter update summary..."
              />
            </div>

            {kind === 'news' && (
              <div className="flex-1 flex flex-col space-y-2 min-h-0">
                <Label>Content</Label>
                <Textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  className="flex-1 min-h-[200px] resize-none"
                  placeholder="Full article content..."
                />
              </div>
            )}
          </div>

          {/* Right side: Document */}
          <div className="w-[400px] shrink-0 flex flex-col">
            <Label className="mb-2">Document</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileSelect}
            />
            {kind === 'news' ? (
              selectedFile ? (
                <div className="flex-1 flex flex-col border-2 border-dashed rounded-lg bg-muted/20">
                  <div className="flex items-center gap-3 p-4 border-b">
                    <FileText size={20} className="text-neutral-500 shrink-0" />
                    <span className="text-sm font-medium truncate flex-1">
                      {selectedFile.name}
                    </span>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="text-neutral-400 hover:text-neutral-600 shrink-0"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                    <p className="text-sm">
                      {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
                    </p>
                    <p className="text-xs mt-1">
                      Will be uploaded after creating the update
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 flex flex-col items-center justify-center border-2 border-dashed rounded-lg bg-muted/20 text-muted-foreground hover:bg-muted/30 hover:border-neutral-400 transition-colors cursor-pointer"
                >
                  <Upload size={48} className="mb-4 opacity-30" />
                  <p className="text-sm text-center px-4">
                    Click to attach a PDF document (optional)
                  </p>
                </button>
              )
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed rounded-lg bg-muted/20 text-muted-foreground">
                <FileText size={48} className="mb-4 opacity-30" />
                <p className="text-sm text-center px-4">
                  Documents can be attached after creating the update
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isPending}>
            {isCreating
              ? 'Creating...'
              : isUploading
                ? 'Uploading file...'
                : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
